import React from 'react';
import Spinner from '../../components/migratorDashboard/Spinner';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { logger } from '../../utils/logger';
import { mapMigrationStatus } from '../../utils/migrationStatus';
import { useFetchJobs, useFetchOrganizations } from '../../hooks/api';
import { useSortedJobs } from '../../hooks/shared';
import { useModalRouting } from '../../hooks/ui';
import { Modal } from '../../components/Modals/Shared/Modal';
import { DeleteModalContent, getMigrationTypeLabel } from '../../components/Modals/Shared/DeleteModalContent';
import { ViewModalContent } from '../../components/Modals/Shared/ViewModalContent';
import { AttentionModalContent } from '../../components/Modals/Shared/AttentionModalContent';
import { RepliCloudFAQsModal } from '../../components/Modals/FAQ/RepliCloudFAQsModal';
import { TableRow } from '../../components/migratorDashboard/TableRow';
import { Tooltip } from '../../components/Tooltip';
import { deleteMigration } from '../../services/Migration/deleteMigrationService';
import { toggleSnapshotActive } from '../../services/Snapshot/toggleSnapshotActiveService';
import { toggleMigrationActive } from '../../services/Migration/toggleMigrationActiveService';
import startOrgSync from '../../services/Migration/startOrgSyncService';
import { useToast } from '../../components/Toast/ToastProvider';
import { useAuth } from '../../hooks/auth';
import type { Migration } from '../../types';
import { dashboardTableConfig, ui } from '../../config/uiSizes';
import useTableLayout from '../../hooks/ui/useTableLayout';
import ErrorAlert from '../../components/errorHandling/ErrorAlert';
import { buildMockJobs } from '../../utils/mockDashboardJobs';

export const MigratorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken, login, logout } = useAuth();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(() => {
    const p = parseInt(searchParams.get('page') ?? '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const isInitialSearchRender = React.useRef(true);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [toggleLoading, setToggleLoading] = React.useState(false);
  const [syncNowLoading, setSyncNowLoading] = React.useState(false);
  const [showFAQModal, setShowFAQModal] = React.useState(false);
  const itemsPerPage = 10;
  const { jobs, setJobs, loading, error, fetchJobs } = useFetchJobs({
    pollingInterval: 30000, // Poll every 30 seconds
    enablePolling: true // Enable automatic polling
  });
  const { organizations, fetchOrganizations } = useFetchOrganizations();

  // Prepend any active dev-only mock jobs (flags default to false — see src/utils/mockDashboardJobs.ts)
  const jobsWithMock = React.useMemo(() => {
    const mocks = buildMockJobs();
    return [...mocks, ...jobs];
  }, [jobs]);

  const { sortedJobs, sortBy, setSortBy, sortDir, setSortDir } = useSortedJobs(jobsWithMock);

  // Log page mount for traceability
  React.useEffect(() => {
    logger.info('Page: MigratorDashboard mounted');
  }, []);

  // When returning from an approve/import/setup action, the caller passes the updated
  // migration in navigation state. Optimistically reflect its new status immediately
  // (the query's staleTime would otherwise show the cached pre-action status), then
  // revalidate in the background and clear the state so a refresh/back doesn't reapply it.
  React.useEffect(() => {
    const navState = location.state as { updatedMigration?: Migration; message?: string } | null;
    const updated = navState?.updatedMigration;
    if (!updated?.id) return;

    setJobs((prev) => {
      const mapped: Migration = { ...updated, status: mapMigrationStatus(updated.status) };
      const idx = prev.findIndex((j) => j.id === mapped.id);
      if (idx === -1) return [mapped, ...prev];
      const next = [...prev];
      next[idx] = { ...next[idx], ...mapped };
      return next;
    });

    if (navState?.message) {
      showToast(navState.message, 'success');
    }

    void fetchJobs();
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location, setJobs, fetchJobs, navigate, showToast]);

  // Fetch organizations on mount
  React.useEffect(() => {
    if (accessToken) {
      fetchOrganizations();
    }
  }, [accessToken, fetchOrganizations]);

  // Handle column header click with unsort support (3-click cycle: asc → desc → default)
  const handleSort = (field: Parameters<typeof setSortBy>[0]) => {
    if (sortBy === field) {
      // Same column clicked - cycle through asc → desc → default
      if (sortDir === 'asc') {
        setSortDir('desc');
      } else {
        // Reset to default sort
        setSortBy('createdAtUtc');
        setSortDir('desc');
      }
    } else {
      // New column clicked - start with ascending
      setSortBy(field);
      setSortDir('asc');
    }
  };
  const { modalVariant, modalJobId, openModal, closeModal } = useModalRouting();

  // Resolve the job referenced by the URL from the fetched list. On deep-link/refresh
  // the list may not be loaded yet, so this stays null until the job appears.
  const modalJob = React.useMemo(
    () => (modalJobId ? jobsWithMock.find((j) => j.id === modalJobId) ?? null : null),
    [modalJobId, jobsWithMock]
  );

  // Rendered modal state is derived entirely from the URL. A job-backed modal only
  // opens once its job resolves (retrying naturally on the next fetch).
  const modalState = React.useMemo(
    () => ({
      isOpen: modalVariant !== null && (modalJobId === null || modalJob !== null),
      variant: modalVariant ?? 'delete',
      job: modalJob
    }),
    [modalVariant, modalJobId, modalJob]
  );

  // Log modal opens for traceability, then push the modal URL entry.
  const openViewModal = React.useCallback(
    (job: Migration) => {
      logger.info('User opened job details modal', { jobId: job.id, jobName: job.name });
      openModal('view', job.id);
    },
    [openModal]
  );

  const openDeleteModal = React.useCallback(
    (job: Migration) => {
      logger.info('User opened delete confirmation modal', { jobId: job.id, jobName: job.name });
      openModal('delete', job.id);
    },
    [openModal]
  );

  const openAttentionModal = React.useCallback(
    (job: Migration) => {
      logger.info('User opened attention modal', { jobId: job.id, jobName: job.name, status: job.status });
      openModal('attention', job.id);
    },
    [openModal]
  );
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Resizable columns — edit widths and text sizes in src/config/responsiveSizes.ts
  const layout = useTableLayout(dashboardTableConfig);

  // Handle approve click - navigate to appropriate page based on status
  const handleApproveClick = React.useCallback(
    (job: Migration) => {
      if (job.status === 'ExportComplete') {
        logger.info('User navigating to ApproveExport', {
          jobId: job.id,
          jobName: job.name,
          status: job.status
        });
        navigate('/ApproveExportPage', { state: { job } });
      } else if (job.status === 'TransformationComplete') {
        logger.info('User navigating to ApproveTransformations', {
          jobId: job.id,
          jobName: job.name,
          status: job.status
        });
        navigate('/ApproveTransformationsPage', { state: { job } });
      }
    },
    [navigate]
  );

  // Handle rollback click — navigates to RollbackInitiation for this migration.
  const handleRollbackClick = React.useCallback(
    (job: Migration) => {
      navigate(`/RollbackInitiation?migrationId=${encodeURIComponent(job.id)}`, { state: { job } });
    },
    [navigate]
  );

  // Handle review click — navigates to RollbackPreview to review & approve the generated plan.
  const handleReviewClick = React.useCallback(
    (job: Migration) => {
      navigate(`/RollbackPreview?migrationId=${encodeURIComponent(job.id)}`, { state: { job } });
    },
    [navigate]
  );

  // Handle sync now click — triggers an immediate org sync run for a recurring migration.
  const handleSyncNowClick = React.useCallback(
    async (job: Migration) => {
      if (!accessToken) return;
      setSyncNowLoading(true);
      try {
        await startOrgSync(accessToken, job.id);
        showToast('Org sync queued successfully', 'success');
        await fetchJobs();
      } catch (error) {
        logger.error('Failed to start org sync', error instanceof Error ? error : new Error(String(error)));
        showToast('Failed to start org sync', 'error');
      } finally {
        setSyncNowLoading(false);
      }
    },
    [accessToken, fetchJobs, showToast]
  );

  // Filter sorted jobs by search term
  const filteredJobs = sortedJobs.filter((job) =>
    job.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Log search results and reset to page 1 when the user changes the search term.
  // Keyed off searchTerm only — keying off list length would also fire when jobs
  // load or poll, wiping a stored ?page (e.g. when returning via back to page 2).
  React.useEffect(() => {
    if (isInitialSearchRender.current) {
      isInitialSearchRender.current = false;
      return;
    }
    const resultCount = filteredJobs.length;
    logger.debug('Search results updated', {
      searchTerm,
      resultCount,
      totalJobs: sortedJobs.length,
      isEmptyResult: resultCount === 0
    });
    setCurrentPage(1);
    setSearchParams((prev) => { prev.delete('page'); return prev; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedJobs = filteredJobs.slice(startIndex, endIndex);

  // Clamp a stored/out-of-range page down once the list is known, without resetting
  // a still-valid page (so back-navigation to ?page=2 is preserved).
  React.useEffect(() => {
    if (totalPages >= 1 && currentPage > totalPages) {
      setCurrentPage(totalPages);
      setSearchParams((prev) => { prev.set('page', String(totalPages)); return prev; }, { replace: true });
    }
  }, [totalPages, currentPage, setSearchParams]);


  const goToPage = (page: number) => {
    const clamped = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(clamped);
    setSearchParams((prev) => { prev.set('page', String(clamped)); return prev; }, { replace: true });
  };

  const handleDeleteConfirm = async () => {
    if (modalState.job && accessToken) {
      setDeleteLoading(true);
      try {
        logger.info('Delete confirmed', {
          jobId: modalState.job.id,
          name: modalState.job.name
        });

        await deleteMigration(accessToken, modalState.job.id);

        logger.info('Migration deleted successfully', {
          jobId: modalState.job.id
        });

        // Refresh the jobs list
        await fetchJobs();

        closeModal();
      } catch (error) {
        logger.error('Failed to delete migration', { error });
        // You could add error notification here
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  // Allowlist: Edit Migration Settings is only meaningful in early/failed states.
  // Using an allowlist ensures new statuses never accidentally show the button.
  const canEditMigrationSettings = (status: Migration['status'] | undefined): boolean => {
    const s = status?.toString();
    return s === 'scheduled' || s === 'Configuring' || s === '0' ||
      s === 'Failed' || s === '13';
  };

  // Refine the return type of getErrorInfo to ensure consistent typing
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getErrorInfo = (job: Migration): {
    stage: string;
    errors: string[] | undefined;
    buttonText: string | null;
    route: string | null;
    state?: { job: Migration };
  } | null => {
    const statusStr = job.status.toString();
    if (statusStr === 'completed' || statusStr === 'PartiallyCompleted') return null;
    // Import/validation failures: route depends on whether transformations were applied.
    // If Job2Transform was skipped (no transformations) → go back to Approve Export.
    // If Job2Transform completed (transformations were applied) → go to Transformation Page.
    // Covers both explicit 'ImportFailed'/'ValidationFailed' statuses and the generic 'Failed'
    // status that the backend emits when an import job errors out.
    const isImportFailure =
      !job.isSnapshot &&
      (
        job.status === 'ImportFailed' ||
        job.status === 'ValidationFailed' ||
        (job.status === 'Failed' && (job.job3Import?.errors?.length ?? 0) > 0)
      );

    if (isImportFailure) {
      const errors = (job.job3Import?.errors?.length ?? 0) > 0
        ? job.job3Import!.errors
        : (job.job1Export?.errors?.length ?? 0) > 0
          ? job.job1Export!.errors
          : (job.job2Transform?.errors?.length ?? 0) > 0
            ? job.job2Transform!.errors
            : [];

      return {
        stage: 'Import',
        errors,
        buttonText: 'Go to Approve Export',
        route: '/ApproveExportPage',
        state: { job }
      };
    }
    if (job.job1Export?.errors && job.job1Export.errors.length > 0 && !job.isSnapshot) {
      return {
        stage: 'Export',
        errors: job.job1Export.errors,
        buttonText: 'Edit Migration Settings',
        route: '/MigrationSetup'
      };
    }
    if (job.job1Export?.errors && job.job1Export.errors.length > 0 && job.isSnapshot) {
      // Snapshots cannot be edited, only show error info
      return {
        stage: 'Export',
        errors: job.job1Export.errors,
        buttonText: null,
        route: null
      };
    }
    if (job.job2Transform?.errors && job.job2Transform.errors.length > 0) {
      return {
        stage: 'Transformation',
        errors: job.job2Transform.errors,
        buttonText: 'Go to Transformation Page',
        route: '/ApproveTransformationsPage',
        state: { job }
      };
    }
    if (job.job3Import?.errors) {
      return {
        stage: 'Import',
        errors: job.job3Import.errors,
        buttonText: 'Go to Approve Export',
        route: '/ApproveExportPage',
        state: { job }
      };
    }
    return null; // Ensure null is returned when no error info is available
  };

  const handleToggleSnapshotActive = async () => {
    if (!modalState.job || !accessToken) return;

    setToggleLoading(true);
    try {
      await toggleSnapshotActive(accessToken, modalState.job.id);
      showToast(
        modalState.job.isActive ? 'Snapshot stopped successfully' : 'Snapshot resumed successfully',
        'success'
      );
      // Refresh the job list to show updated status
      await fetchJobs();
      closeModal();
    } catch (error) {
      logger.error('Error toggling snapshot', error instanceof Error ? error : new Error(String(error)));
      showToast('Failed to toggle snapshot status', 'error');
    } finally {
      setToggleLoading(false);
    }
  };

  const handleToggleMigrationActive = async () => {
    if (!modalState.job || !accessToken) return;

    setToggleLoading(true);
    try {
      await toggleMigrationActive(accessToken, modalState.job.id);
      showToast(
        modalState.job.isActive ? 'Migration stopped successfully' : 'Migration resumed successfully',
        'success'
      );
      // Refresh the job list to show updated status
      await fetchJobs();
      closeModal();
    } catch (error) {
      logger.error('Error toggling migration', error instanceof Error ? error : new Error(String(error)));
      showToast('Failed to toggle migration status', 'error');
    } finally {
      setToggleLoading(false);
    }
  };

  const handleLoginAgain = React.useCallback(() => {
    logout();
    login();
  }, [login, logout]);

  const isForbiddenError = error === 'forbidden';

  // Sort icon
  const SortIcon = ({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) => (
    <span className={`inline-block ml-1 text-gray-400 ${active ? '' : 'opacity-30'}`}>
      {dir === 'asc' ? '▲' : '▼'}
    </span>
  );

  return (
    <div className={ui.page.padding}>
      <div className="flex flex-col flex-1 rounded-2xl border border-gray-300 bg-gray-200 shadow-xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden mx-2 mt-0 mb-2 p-2">
        {/* Dashboard card */}
        <div
          className={`bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 h-full flex flex-col ${ui.page.cardPadding}`}
        >
          {/* Title */}
          <h2
            className={`text-left font-extrabold text-gray-800 dark:text-gray-200 mb-1 ${ui.text.pageTitle}`}
          >
            Dashboard View
          </h2>
        </div>
        {/* Subtitle with colored links */}
        <div className="flex-1 flex flex-col min-h-0 h-full overflow-hidden" style={{ minHeight: 0 }}>
          <section
            className={`flex flex-col overflow-hidden rounded-xl border border-gray-300 bg-gray-100 shadow-md dark:border-gray-700 dark:bg-gray-800 ${ui.page.sectionPadding}`}
          >
            <div className={`flex items-center gap-2 ${ui.mb.control}`}>
              <div className="flex items-center gap-3">
                <h2
                  className={`text-left font-extrabold text-gray-800 dark:text-gray-100 ${ui.text.sectionTitle}`}
                >
                  Migrations & Snapshots
                </h2>
                <div className="relative group/overview cursor-default">
                  <span
                    className={`inline-block bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200 font-bold rounded-full ${ui.badge.sm}`}
                  >
                    Overview
                  </span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 hidden group-hover/overview:block z-50 pointer-events-none w-80">
                    <div className="w-2 h-2 bg-gray-900 dark:bg-gray-900 rotate-45 mx-auto -mb-1" />
                    <div
                      className={`bg-gray-900 dark:bg-gray-900 text-white ${ui.text.sm} font-medium px-2.5 py-1.5 rounded-lg shadow-lg leading-snug text-center`}
                    >
                      View, manage, and track all your migrations and snapshots in one place. Search
                      or sort by name, type, creator, organization, or status. Use the action
                      buttons to approve pending jobs, inspect details, or delete a migration.
                      <div className="mt-2 pt-2 border-t border-gray-700 text-left space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400 shrink-0" />
                          <span>Green icon — Export Complete, pending approval</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-blue-400 shrink-0" />
                          <span>Blue icon — Transformation Complete, pending approval</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={`mb-4 ${ui.mb.control} relative`}>
              <input
                type="text"
                placeholder="Search by migration name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full ${ui.input.base} rounded-lg border border-gray-300 bg-white text-gray-700 placeholder-gray-500 shadow-sm transition focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:placeholder-gray-400 ${searchTerm ? 'pr-10' : ''}`}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    logger.info('Search cleared');
                    setSearchTerm('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                  aria-label="Clear search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Error banner — shown when the data fetch fails. Message is intentionally generic. */}
            {error && (
              <div className={ui.mb.control}>
                <ErrorAlert message={error} onRetry={() => fetchJobs()} />
                {isForbiddenError && (
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={handleLoginAgain}
                      className={`${ui.button.sm} bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-lg transition-colors font-semibold`}
                    >
                      Login again
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="rounded-xl border border-gray-200 bg-white shadow-sm flex-1 min-h-0 flex flex-col dark:border-gray-800 dark:bg-gray-900 text-xs">
              <div ref={scrollContainerRef} className="flex-1 min-h-0 pb-2 overflow-y-auto">
                {loading ? (
                  <Spinner />
                ) : (
                  <table className="w-full text-left mb-2 table-fixed">
                    <colgroup>
                      {layout.colWidths.map((w, i) => (
                        <col key={i} style={{ width: `${w}px` }} />
                      ))}
                    </colgroup>
                    <thead className="sticky top-0 z-10 bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                      <tr className="border-b border-gray-200 dark:border-gray-700/60">
                        <th
                          className={` ${ui.input.custom} ${layout.config.headerTextSizeClass} font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap relative group`}
                          onClick={() => handleSort('name')}
                        >
                          <span className="flex items-center pr-4">
                            Name
                            <SortIcon active={sortBy === 'name'} dir={sortDir} />
                          </span>
                          <div
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 dark:bg-gray-600 opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-blue-400 dark:hover:bg-blue-500 transition-opacity"
                            onMouseDown={(e) => layout.handleResizeMouseDown(e, 0)}
                          />
                        </th>
                        <th
                          className={`${ui.input.custom} ${layout.config.headerTextSizeClass} font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap relative group`}
                          onClick={() => handleSort('type')}
                        >
                          <span className="flex items-center pr-4">
                            Type
                            <SortIcon active={sortBy === 'type'} dir={sortDir} />
                          </span>
                          <div
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 dark:bg-gray-600 opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-blue-400 dark:hover:bg-blue-500 transition-opacity"
                            onMouseDown={(e) => layout.handleResizeMouseDown(e, 1)}
                          />
                        </th>
                        <th
                          className={`${ui.input.custom} ${layout.config.headerTextSizeClass} font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap relative group`}
                          onClick={() => handleSort('createdBy')}
                        >
                          <span className="flex items-center pr-4">
                            <span className="hidden xl:inline">Created By</span>
                            <span className="xl:hidden">Created</span>
                            <SortIcon active={sortBy === 'createdBy'} dir={sortDir} />
                          </span>
                          <div
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 dark:bg-gray-600 opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-blue-400 dark:hover:bg-blue-500 transition-opacity"
                            onMouseDown={(e) => layout.handleResizeMouseDown(e, 2)}
                          />
                        </th>
                        <th
                          className={`${ui.input.custom} ${layout.config.headerTextSizeClass} font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap relative group`}
                          onClick={() => handleSort('sourceOrganization')}
                        >
                          <span className="flex items-center pr-4">
                            <span className="hidden xl:inline">Source Org</span>
                            <span className="xl:hidden">Source</span>
                            <SortIcon active={sortBy === 'sourceOrganization'} dir={sortDir} />
                          </span>
                          <div
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 dark:bg-gray-600 opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-blue-400 dark:hover:bg-blue-500 transition-opacity"
                            onMouseDown={(e) => layout.handleResizeMouseDown(e, 3)}
                          />
                        </th>
                        <th
                          className={`${ui.input.custom} ${layout.config.headerTextSizeClass} font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap relative group`}
                          onClick={() => handleSort('targetOrganization')}
                        >
                          <span className="flex items-center pr-4">
                            <span className="hidden xl:inline">Target Org</span>
                            <span className="xl:hidden">Target</span>
                            <SortIcon active={sortBy === 'targetOrganization'} dir={sortDir} />
                          </span>
                          <div
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 dark:bg-gray-600 opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-blue-400 dark:hover:bg-blue-500 transition-opacity"
                            onMouseDown={(e) => layout.handleResizeMouseDown(e, 4)}
                          />
                        </th>
                        <th
                          className={`${ui.input.custom} ${layout.config.headerTextSizeClass} font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap relative group`}
                          onClick={() => handleSort('lastRun')}
                        >
                          <span className="flex items-center pr-4">
                            <span className="hidden xl:inline">Last Run</span>
                            <span className="xl:hidden">Run</span>
                            <SortIcon active={sortBy === 'lastRun'} dir={sortDir} />
                          </span>
                          <div
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 dark:bg-gray-600 opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-blue-400 dark:hover:bg-blue-500 transition-opacity"
                            onMouseDown={(e) => layout.handleResizeMouseDown(e, 5)}
                          />
                        </th>
                        <th
                          className={`${ui.input.custom} ${layout.config.headerTextSizeClass} font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap relative group`}
                          onClick={() => handleSort('status')}
                        >
                          <span className="flex items-center pr-4">
                            Status
                            <SortIcon active={sortBy === 'status'} dir={sortDir} />
                          </span>
                          <div
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-gray-300 dark:bg-gray-600 opacity-0 group-hover:opacity-100 hover:!opacity-100 hover:bg-blue-400 dark:hover:bg-blue-500 transition-opacity"
                            onMouseDown={(e) => layout.handleResizeMouseDown(e, 6)}
                          />
                        </th>
                        <th className="px-2 py-2 text-[13px] font-semibold uppercase tracking-wider select-none"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-800 dark:text-gray-300">
                      {!error &&
                        paginatedJobs.map((job) => (
                          <TableRow
                            key={job.id}
                            job={job}
                            organizations={organizations}
                            onViewClick={openViewModal}
                            onDeleteClick={openDeleteModal}
                            onApproveClick={handleApproveClick}
                            onAttentionClick={openAttentionModal}
                            onRollbackClick={handleRollbackClick}
                            onReviewClick={handleReviewClick}
                          />
                        ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <span className={`${ui.text.pagination} text-gray-700 dark:text-gray-300`}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <span className={`${ui.text.pagination} text-gray-500 dark:text-gray-400`}>
                      ({filteredJobs.length} total migrations)
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => goToPage(1)}
                      disabled={currentPage === 1}
                      className={` ${ui.button.pagination} rounded-lg bg-gray-200 text-gray-700 font-semibold transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${ui.text.pagination}`}
                    >
                      First
                    </button>
                    <button
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`${ui.button.pagination} rounded-lg bg-gray-200 text-gray-700 font-semibold transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 ${ui.text.pagination}`}
                    >
                      Previous
                    </button>

                    {/* Page numbers */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => goToPage(pageNum)}
                            className={`${ui.button.pagination} rounded-lg font-semibold transition ${currentPage === pageNum
                              ? 'bg-blue-600 text-white dark:bg-blue-700'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                              }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`${ui.button.pagination} rounded-lg bg-gray-200 text-gray-700 font-semibold transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600`}
                    >
                      Next
                    </button>
                    <button
                      onClick={() => goToPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className={`${ui.button.pagination} rounded-lg bg-gray-200 text-gray-700 font-semibold transition hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600`}
                    >
                      Last
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={modalState.isOpen}
        variant={modalState.variant}
        title={
          modalState.variant === 'delete'
            ? `Delete ${modalState.job ? getMigrationTypeLabel(modalState.job).replace(/^\w/, c => c.toUpperCase()) : 'Migration'}`
            : modalState.job?.isSnapshot
              ? (
                <span className="inline-flex items-center justify-center gap-1">
                  Snapshot Details
                  <Tooltip
                    variant="amber"
                    position="bottom"
                    content={
                      <div className="space-y-2 text-left">
                        <p className="font-semibold">About snapshots</p>
                        <div className="space-y-1.5">
                          <div className="flex items-start gap-2">
                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                            <div>
                              <span className="font-semibold">What a snapshot is — </span>
                              A point-in-time capture of your Genesys Cloud objects and resources.
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                            <div>
                              <span className="font-semibold">Where snapshot files are stored — </span>
                              In RepliCloud, accessible from this dashboard. Download them at any time.
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                            <div>
                              <span className="font-semibold">Who uses snapshots (admins/developers) — </span>
                              Administrators and developers use snapshots to review resources, check
                              dependencies, and reference settings for manual restore.
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  />
                </span>
              )
              : 'Migration Details'
        }
        onClose={() => {
          logger.info('Modal closed', { type: 'job-details', action: 'cancelled' });
          closeModal();
        }}
        {...(modalState.variant === 'delete' && {
          onConfirm: handleDeleteConfirm,
          confirmLabel: 'Delete',
          showConfirmButton: true,
          confirmLoading: deleteLoading,
          confirmDisabled: deleteLoading,
          confirmLoadingLabel: 'Deleting...'
        })}
        {...(modalState.variant === 'view' && {
          showConfirmButton: false,
          customButtons: (
            <>
              {modalState.job?.snapshotCronExpression && (
                <button
                  onClick={modalState.job?.isSnapshot ? handleToggleSnapshotActive : handleToggleMigrationActive}
                  disabled={toggleLoading}
                  className={`flex-1 rounded-lg ${modalState.job?.isActive
                    ? 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600'
                    : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
                    } px-4 py-2 text-white font-semibold shadow transition flex items-center justify-center gap-2 ${toggleLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  type="button"
                >
                  {toggleLoading ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 text-white"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      {modalState.job?.isActive ? (
                        <>
                          <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>Stop Snapshot</span>
                        </>
                      ) : (
                        <>
                          <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>Resume Snapshot</span>
                        </>
                      )}
                    </>
                  )}
                </button>
              )}
              {!modalState.job?.isSnapshot &&
                canEditMigrationSettings(modalState.job?.status) && (
                  <button
                    onClick={() => {
                      navigate('/MigrationSetup', {
                        state: {
                          job: modalState.job,
                          migrationData: {
                            name: modalState.job?.name,
                            sourceOrgName: modalState.job?.sourceOrgName,
                            targetOrgName: modalState.job?.targetOrgName,
                            sourceOrgId: modalState.job?.sourceOrgId,
                            targetOrgId: modalState.job?.targetOrgId,
                            resources: modalState.job?.resources,
                            id: modalState.job?.id,
                            isPrePopulatedForm: true
                          }
                        }
                      });
                    }}
                    className={`flex-1 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white font-semibold ${ui.button.modalButton} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                    type="button"
                  >
                    <svg
                      className={`${ui.svg.sm}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span>Edit Migration Settings</span>
                  </button>
                )}
              {!modalState.job?.isSnapshot && (
                <button
                  onClick={() => setShowFAQModal(true)}
                  className={`flex-1 bg-teal-600 hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-600 text-white font-semibold ${ui.button.modalButton} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                  type="button"
                >
                  <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Need help? FAQ
                </button>
              )}
            </>
          )
        })}
        {...(modalState.variant === 'view' &&
          modalState.job && {
          showConfirmButton: false,
          customButtons: (
            <div className={`flex ${ui.gap.md} w-full`}>
              {modalState.job?.isSnapshot && modalState.job?.snapshotCronExpression && (
                <button
                  onClick={handleToggleSnapshotActive}
                  disabled={toggleLoading}
                  className={`flex-1 rounded-lg ${modalState.job?.isActive
                    ? 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600'
                    : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
                    } ${ui.button.modalButton} text-white font-semibold shadow transition flex items-center justify-center gap-2 ${toggleLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  type="button"
                >
                  {toggleLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : modalState.job?.isActive ? (
                    <>
                      <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                      </svg>
                      <span>Stop Snapshot</span>
                    </>
                  ) : (
                    <>
                      <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      <span>Resume Snapshot</span>
                    </>
                  )}
                </button>
              )}
              {!modalState.job?.isSnapshot && modalState.job?.snapshotCronExpression && (
                <button
                  onClick={handleToggleMigrationActive}
                  disabled={toggleLoading}
                  className={`flex-1 rounded-lg ${modalState.job?.isActive
                    ? 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600'
                    : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
                    } ${ui.button.modalButton} text-white font-semibold shadow transition flex items-center justify-center gap-2 ${toggleLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                  type="button"
                >
                  {toggleLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      <span>Processing...</span>
                    </>
                  ) : modalState.job?.isActive ? (
                    <>
                      <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                      </svg>
                      <span>Stop Migration</span>
                    </>
                  ) : (
                    <>
                      <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      <span>Resume Migration</span>
                    </>
                  )}
                </button>
              )}
              {!modalState.job?.isSnapshot && (
                <button
                  onClick={() => setShowFAQModal(true)}
                  className={`flex-1 bg-teal-600 hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-600 text-white font-semibold ${ui.button.modalButton} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                  type="button"
                >
                  <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Need help? FAQ
                </button>
              )}
            </div>
          )
        })}
        {...(modalState.variant === 'attention' &&
          modalState.job &&
          modalState.job.snapshotCronExpression && {
          showConfirmButton: false,
          cancelLabel: undefined,
          customButtons: (
            <div className={`flex ${ui.gap.md} w-full`}>
              <button
                onClick={modalState.job?.isSnapshot ? handleToggleSnapshotActive : handleToggleMigrationActive}
                disabled={toggleLoading}
                className={`flex-1 rounded-lg ${modalState.job?.isActive
                  ? 'bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600'
                  : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
                  } ${ui.button.modalButton} text-white font-semibold shadow transition flex items-center justify-center gap-2 ${toggleLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                type="button"
              >
                {toggleLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    <span>Processing...</span>
                  </>
                ) : modalState.job?.isActive ? (
                  <>
                    <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                    </svg>
                    <span>{modalState.job?.isSnapshot ? 'Stop Snapshot' : 'Stop Migration'}</span>
                  </>
                ) : (
                  <>
                    <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                    <span>{modalState.job?.isSnapshot ? 'Resume Snapshot' : 'Resume Migration'}</span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  navigate('/MigrationLogs', { state: { job: modalState.job } });
                }}
                className={`flex-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-semibold ${ui.button.modalButton} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                type="button"
              >
                <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                    clipRule="evenodd"
                  />
                </svg>
                View Detailed Logs
              </button>
            </div>
          )
        })}
        {...(modalState.variant === 'attention' &&
          modalState.job &&
          !modalState.job.snapshotCronExpression && {
          showConfirmButton: false,
          cancelLabel: undefined,
          customButtons: (
            <div className={`flex ${ui.gap.md} w-full`}>
              <button
                onClick={() => {
                  navigate('/MigrationLogs', { state: { job: modalState.job } });
                }}
                className={`flex-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-semibold ${ui.button.modalButton} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                type="button"
              >
                <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>View Logs</span>
              </button>
              <button
                onClick={() => setShowFAQModal(true)}
                className={`flex-1 bg-teal-600 hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-600 text-white font-semibold ${ui.button.modalButton} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                type="button"
              >
                <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Read FAQ</span>
              </button>
            </div>
          )
        })}
        cancelLabel="Cancel"
        showCloseButton={true}
      >

        {
          ((): React.ReactNode => {
            if (modalState.variant === 'delete' && modalState.job) {
              return <DeleteModalContent job={modalState.job} />;
            }
            if (modalState.variant === 'view' && modalState.job) {
              return (
                <ViewModalContent
                  job={modalState.job}
                  onClose={closeModal}
                  onSyncNow={() => handleSyncNowClick(modalState.job!)}
                  syncNowLoading={syncNowLoading}
                />
              );
            }
            if (modalState.variant === 'attention' && modalState.job) {
              return (
                <AttentionModalContent
                  job={modalState.job}
                  onClose={closeModal}
                  onSyncNow={() => handleSyncNowClick(modalState.job!)}
                  syncNowLoading={syncNowLoading}
                />
              );
            }
            return null;
          })() as React.ReactNode
        }
      </Modal>

      <RepliCloudFAQsModal
        isOpen={showFAQModal}
        onClose={() => setShowFAQModal(false)}
        onBack={() => setShowFAQModal(false)}
      />
    </div>
  );
};
